///
/// EarthBrowser
///
/// Created by Matt Giger on 8/20/2024
/// Copyright (c) 2024 Geodata Labs
///

import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { EarthTile } from "./EarthTile"
import { type TileCandidate } from "./EarthTileUpdateTask"

export class EarthTileSet {
	private context: EarthBrowserContext
	public readonly baseLevel: number
	private loadingQueueLength: number
	private purgableTimeSecs: number
	private tiles = new Map<string, EarthTile>()
	private wanted: TileCandidate[] = []

	constructor(context: EarthBrowserContext, baseLevel: number, loadingQueueLength: number, purgableTimeSecs: number) {
		this.context = context
		this.baseLevel = baseLevel
		this.loadingQueueLength = loadingQueueLength
		this.purgableTimeSecs = purgableTimeSecs
	}

	public addBaseTile(tile: EarthTile): void {
		this.context.logger.assert(tile.tileId.z == this.baseLevel - 1, "bad base tile level")
		this.tiles.set(tile.tileId.key, tile)
	}

	/**
	 * Replace the set of tiles the camera currently wants. The list is kept sorted
	 * coarse-first, then by distance to the view center, so queueLoading creates
	 * parents before their children and fills the middle of the screen first.
	 */
	public updateWanted(wanted: TileCandidate[]): void {
		this.wanted = wanted.sort((a, b) => (a.id.z - b.id.z) || (a.centerDistance - b.centerDistance))

		const now = performance.now() * 0.001
		this.allTiles().forEach(tile => { tile.wanted = false })
		this.wanted.forEach(candidate => {
			const tile = this.tiles.get(candidate.id.key)
			if(tile) {
				tile.wanted = true
				tile.centerDistance = candidate.centerDistance
				tile.lastAccess = now
				// Zoomed back in before the eviction fade finished
				tile.material.cancelFadeOut()
			}
		})
	}

	private tileExpired(tile: EarthTile, now: number): boolean {
		const expired =
			tile.tileId.z >= this.baseLevel &&
			!tile.wanted &&
			tile.lastAccess < now - this.purgableTimeSecs
		return expired
	}

	public pruneDormantTiles(): void {
		const now = performance.now() * 0.001

		// Never prune a tile whose textures a surviving child still samples:
		// children blend the parent's imagery during fades and sample the
		// parent's elevation for their whole lifetime when they have no
		// elevation of their own. Destroying it would leave the child binding
		// a deleted GL texture, which silently no-ops and renders whatever
		// texture the previous draw left on that unit. Protection propagates
		// up the ancestor chain (finest-to-coarsest) since a kept child keeps
		// its parent alive, which in turn still samples the grandparent.
		//
		// Eviction is hidden behind a fade: an expired tile first blends its
		// imagery and elevation back to the parent's, and is destroyed only
		// once that completes, so the parent's section reopening changes
		// nothing on screen. A fading tile still protects its parent, so a
		// dormant subtree is released a level at a time from the leaves up.
		const sorted = this.allTiles().sort((a, b) => b.tileId.z - a.tileId.z)
		const protectedKeys = new Set<string>()
		const purgable: EarthTile[] = []
		sorted.forEach(tile => {
			const material = tile.material
			const expired = this.tileExpired(tile, now) && !protectedKeys.has(tile.tileId.key)
			if(!expired) {
				material.cancelFadeOut()
			} else if(!material.canFadeOut || material.fadeOutComplete) {
				purgable.push(tile)
				return
			} else {
				material.startFadeOut()
			}
			protectedKeys.add(tile.tileId.parent.key)
		})

		purgable.forEach(tile => {
			tile.destroy()
			this.tiles.delete(tile.tileId.key)
		})
	}

	/**
	 * Create missing wanted tiles, keeping at most loadingQueueLength loads in
	 * flight. The wanted list is ordered coarse-to-fine and center-out, and a tile
	 * is only created once its parent can provide fade/elevation data, so imagery
	 * refines level by level without gaps.
	 */
	public queueLoading(): void {
		const now = performance.now() * 0.001

		let loadingCount = this.loading().length
		if(loadingCount >= this.loadingQueueLength) {
			return
		}

		for(const candidate of this.wanted) {
			if(!this.tiles.has(candidate.id.key)) {
				const parentTile = this.tiles.get(candidate.id.parent.key)
				if(parentTile?.readyToBeParent) {
					const newTile = new EarthTile(this.context, candidate.id, undefined, parentTile.material)
					newTile.wanted = true
					newTile.centerDistance = candidate.centerDistance
					newTile.lastAccess = now
					this.tiles.set(candidate.id.key, newTile)
					if(++loadingCount >= this.loadingQueueLength) {
						return
					}
				}
			}
		}
	}

	/**
	 * Build the list of tiles to draw this frame, capped at maxTiles.
	 *
	 * Section masks are computed against only the tiles actually being rendered, so a
	 * tile culled by the cap never leaves a hole: its parent (or ultimately a base tile)
	 * section re-opens and shows lower resolution imagery until the finer tile renders.
	 */
	public renderList(maxTiles: number): EarthTile[] {
		const allTiles = this.renderable()
		const baseTiles = allTiles.filter(tile => tile.tileId.z < this.baseLevel)
		const detailTiles = allTiles.filter(tile => tile.tileId.z >= this.baseLevel)

		// When over budget, keep tiles the camera currently wants preferring lower z
		// for coverage and view-center proximity, then fill the remaining budget
		// with the most recently accessed tiles
		const maxDetailTiles = Math.max(0, maxTiles - baseTiles.length)
		if(detailTiles.length > maxDetailTiles) {
			detailTiles.sort((a, b) => {
				if(a.wanted !== b.wanted) return a.wanted ? -1 : 1
				if(a.wanted) return (a.tileId.z - b.tileId.z) || (a.centerDistance - b.centerDistance)
				return b.lastAccess - a.lastAccess
			})
			detailTiles.splice(maxDetailTiles)
		}

		// Recompute parent/child section visibility from the rendered set
		const rendered = new Map<string, EarthTile>()
		baseTiles.forEach(tile => rendered.set(tile.tileId.key, tile))
		detailTiles.forEach(tile => rendered.set(tile.tileId.key, tile))
		rendered.forEach(tile => tile.calculatePatches(rendered))

		// Tiles whose sections are all covered by loaded children draw nothing -
		// skip them (they stay cached and re-enter the list when a child leaves)
		const drawnDetail = detailTiles.filter(tile => tile.sectionsToRender !== 0)
		const drawnBase = baseTiles.filter(tile => tile.sectionsToRender !== 0)

		// Draw detail tiles in decreasing z order, then the always-present base tiles
		drawnDetail.sort((a, b) => b.tileId.z - a.tileId.z)
		return [...drawnDetail, ...drawnBase]
	}

	public allTiles(): EarthTile[] {
		return Array.from(this.tiles.values())
	}

	public loading(): EarthTile[] {
		const loading = this.allTiles().filter(tile => !tile.material.readyToBeAParent)
		return loading
	}

	public renderable(): EarthTile[] {
		return this.allTiles().filter(tile => tile.material.textureLoaded)
	}
}
